import React, { useState } from "react";
import {
  IconButton, Popover, Card, CardContent, Typography, Avatar,
  List, ListItem, ListItemIcon, ListItemText, Divider, Tooltip,
  useMediaQuery, useTheme
} from "@mui/material";
import { Person, ExitToApp, AccountCircle } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import API, { clearAuthTokens } from "../../services/api";
import { useAuth } from "../../auth/AuthProvider";
import { useDispatch } from 'react-redux';
import { clearAllTabs, setActiveTabByPath } from '../../tabs/tabsSlice';

const UserMenu = () => {
  const [anchorEl, setAnchorEl] = useState(null);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const storedUserData = JSON.parse(localStorage.getItem("userData"));
  const { user } = useAuth();
  const absolutize = (url) => {
    if (!url || typeof url !== "string") return null;
    if (/^https?:\/\//i.test(url)) return url;
    const base = (process.env.REACT_APP_API_BASE || "http://localhost:8000/").replace(/\/+$/, "");
    return `${base}/${url.replace(/^\/+/, "")}`;
  };
  const avatar = absolutize((user && user.avatar) || (storedUserData?.avatar));
  const { hardResetAuth } = useAuth();

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const handleOpen = (e) => setAnchorEl(e.currentTarget);
  const handleClose = () => setAnchorEl(null);

  const handleLogOut = async () => {
    handleClose(); // ✅ cierra el popover para feedback inmediato

    // revocar refresh opcional (no bloqueante)
    const withTimeout = (p, ms = 3500) =>
      Promise.race([p, new Promise((_, rej) => setTimeout(() => rej(new Error("timeout")), ms))]);

    try {
      const refreshToken = localStorage.getItem("refresh_token");
      if (refreshToken) {
        await withTimeout(API.post("auth/logout/", { refresh_token: refreshToken }));
      }
    } catch (_) {}

    try { sessionStorage.clear(); } catch {}
    try { localStorage.removeItem("userData"); } catch {}

    clearAuthTokens();
    try { dispatch(clearAllTabs()); dispatch(setActiveTabByPath(null)); } catch {}
    hardResetAuth();

    // navegación suave (sin recarga, sin fallback extra que pueda parpadear)
    navigate("/login", { replace: true });

    // limpieza de caches en idle
    const clearCachesAsync = async () => {
      if (!("caches" in window)) return;
      try {
        const names = await caches.keys();
        await Promise.allSettled(names.map((n) => caches.delete(n)));
      } catch {}
    };
    if ("requestIdleCallback" in window) requestIdleCallback(clearCachesAsync);
    else setTimeout(clearCachesAsync, 0);
  };

  return (
    <>
      <Tooltip title="Menú de usuario">
        <IconButton onClick={handleOpen} sx={{ color: "white", "&:hover": { backgroundColor: "rgba(47,255,186,0.1)" } }}>
          <Person />
        </IconButton>
      </Tooltip>

      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{ sx: { backgroundColor: "transparent", boxShadow: "none" } }}
      >
        <Card sx={{ width: isMobile ? 220 : 260, background: "linear-gradient(135deg,#1e2746,#10173c)", color: "white", borderRadius: 3, boxShadow: 8, p: 2 }}>
          <CardContent sx={{ textAlign: "center", pb: 1 }}>
            <Avatar
              src={avatar || undefined}
              sx={{ width: isMobile ? 50 : 70, height: isMobile ? 50 : 70, m: "auto", mb: 1, bgcolor: "#00bcd4", fontSize: isMobile ? 20 : 28 }}
            >
              {!avatar && (<>{storedUserData?.first_name?.[0]}{storedUserData?.last_name?.[0]}</>)}
            </Avatar>
            <Typography variant="subtitle1" sx={{ fontWeight: "bold", fontSize: isMobile ? "0.9rem" : "1rem" }}>
              {storedUserData?.first_name} {storedUserData?.last_name}
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.75, fontSize: isMobile ? "0.75rem" : "0.875rem" }}>
              {storedUserData?.email}
            </Typography>
          </CardContent>

          <Divider sx={{ my: 1, borderColor: "rgba(255,255,255,0.1)" }} />

          <List dense>
            <ListItem
              button
              onClick={() => { navigate("/user-profile"); handleClose(); }}
              sx={{ "&:hover": { backgroundColor: "rgba(255,255,255,0.1)" }, borderRadius: 2 }}
            >
              <ListItemIcon sx={{ color: "#90caf9" }}><AccountCircle /></ListItemIcon>
              <ListItemText primary="Ver Perfil" />
            </ListItem>

            <ListItem
              button
              onClick={handleLogOut}
              sx={{ "&:hover": { backgroundColor: "rgba(255,255,255,0.1)" }, borderRadius: 2 }}
            >
              <ListItemIcon sx={{ color: "#ef5350" }}><ExitToApp /></ListItemIcon>
              <ListItemText primary="Cerrar sesión" />
            </ListItem>
          </List>
        </Card>
      </Popover>
    </>
  );
};

export default UserMenu;
